import {useEffect, useState} from "react";
import {Ban, FileQuestion, KeyRound, RefreshCw, RotateCw, Send, ShieldAlert} from "lucide-react";
import {getActionPresentation, getVisibleActions, hasCompletePublishedGraph} from "../statusMachine";
import {useTranslation} from "../../../shared/i18n";


const ACTION_ICON = {
    request_documents: FileQuestion,
    reject: Ban,
    publish: Send,
    rotate_access_token: RotateCw,
    rotate_credentials: KeyRound,
};

const CONNECTED_ACTIONS = new Set([
    "request_documents",
    "reject",
    "publish",
    "rotate_access_token",
    "rotate_credentials",
]);

export const ModerationActions = ({
    application,
    disabled,
    mutationState,
    onOpenAction,
    onRetry,
    onRefresh,
}) => {
    const {t} = useTranslation();
    const [now, setNow] = useState(Date.now);
    const completeGraph = hasCompletePublishedGraph(application.resources);
    const visibleActions = getVisibleActions(application.status, application.allowed_actions).filter(
        (action) =>
            ACTION_ICON[action] && (action !== "rotate_credentials" || completeGraph),
    );
    const pending = mutationState.status === "pending";
    const retryPending = mutationState.retryAvailableAt > now;
    const retrySeconds = retryPending
        ? Math.max(1, Math.ceil((mutationState.retryAvailableAt - now) / 1000))
        : 0;

    useEffect(() => {
        if (!mutationState.retryAvailableAt || mutationState.retryAvailableAt <= Date.now()) {
            return undefined;
        }
        setNow(Date.now());
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [mutationState.retryAvailableAt]);

    return (
        <div
            className="onboarding-actions-reserved"
            role="group"
            aria-label={t("sections.onboarding.p116")}
            aria-busy={pending}
        >
            <h3>{t("sections.onboarding.p117")}</h3>

            {visibleActions.length > 0 ? (
                <div className="onboarding-action-list">
                    {visibleActions.map((actionId) => {
                        const presentation = getActionPresentation(actionId);
                        const Icon = ACTION_ICON[actionId];
                        const connected = CONNECTED_ACTIONS.has(actionId);
                        return (
                            <button
                                key={actionId}
                                className={`admin-button onboarding-action-${presentation.tone}`}
                                type="button"
                                disabled={disabled || pending || !connected}
                                onClick={(event) => onOpenAction(actionId, event.currentTarget)}
                            >
                                <Icon aria-hidden="true" size={16} />
                                {pending && mutationState.actionId === actionId
                                    ? t("sections.onboarding.p118")
                                    : presentation.label}
                            </button>
                        );
                    })}
                </div>
            ) : (
                <p>{t("sections.onboarding.p119")}</p>
            )}

            {disabled && !pending ? (
                <p className="onboarding-action-disabled-reason" role="status">
                    {t("sections.onboarding.p120")}
                </p>
            ) : null}

            {application.status === "published" && !hasCompletePublishedGraph(application.resources) ? (
                <div className="onboarding-action-message onboarding-action-message-danger">
                    <ShieldAlert aria-hidden="true" size={16} />
                    {t("sections.onboarding.p121")}
                </div>
            ) : null}

            {mutationState.status === "success" ? (
                <p className="onboarding-action-success" role="status" aria-live="polite">
                    {mutationState.replayWithoutSecret
                        ? t("sections.onboarding.p122")
                        : t("sections.onboarding.p123")}
                </p>
            ) : null}

            {mutationState.status === "error" && !mutationState.dialogError ? (
                <div className="onboarding-action-result" role="alert">
                    <strong>
                        {mutationState.uncertain ? t("sections.onboarding.p124") : t("sections.onboarding.p125")}
                    </strong>
                    <p>{mutationState.error?.message}</p>
                    {mutationState.error?.requestId ? (
                        <code>{mutationState.error.requestId}</code>
                    ) : null}
                    <div>
                        {mutationState.canRetry ? (
                            <button
                                className="admin-button admin-button-primary"
                                type="button"
                                disabled={retryPending || pending}
                                onClick={onRetry}
                            >
                                <RefreshCw aria-hidden="true" size={15} />
                                {retryPending
                                    ? t("sections.onboarding.p126", {v0: retrySeconds})
                                    : t("sections.onboarding.p127")}
                            </button>
                        ) : null}
                        <button
                            className="admin-button admin-button-secondary"
                            type="button"
                            disabled={pending}
                            onClick={onRefresh}
                        >
                            <RefreshCw aria-hidden="true" size={15} />
                            {t("sections.onboarding.p128")}
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
};
